import * as React from "react";
import { cn, useCopyToClipboard } from "@aisquare/ui";
import { CheckIcon, CopyIcon } from "lucide-react";
import {
  importNamesFor,
  LATEST_VERSION,
  layerMetaFor,
  type DemoEntry,
  type LayerMeta,
} from "./registry";

/**
 * Small shared chrome for the playground shell — the brand mark, key caps,
 * copy affordance, badges and eyebrow. Kept in one module because each of these
 * is a handful of lines used by three or four different surfaces (header,
 * sidebar, command palette, component page, overview), and scattering them
 * meant the same chip was written slightly differently in each place.
 */

/** Mac shows ⌘, everything else Ctrl, in every shortcut hint on the page. */
export const IS_MAC =
  typeof navigator !== "undefined" &&
  /mac|iphone|ipad|ipod/i.test(navigator.platform || navigator.userAgent);

/**
 * Inline key cap. The library's `Kbd` isn't exported from the barrel, so the
 * playground carries its own — mono, tabular, sized to sit inside a 32px row.
 */
export function Kbd({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <kbd
      className={cn(
        "inline-flex h-5 min-w-5 items-center justify-center rounded border border-border bg-muted px-1.5",
        "font-mono text-[10px] font-medium leading-none text-muted-foreground",
        className,
      )}
    >
      {children}
    </kbd>
  );
}

/**
 * Click-to-copy with a brief confirmation, on the library's own
 * `useCopyToClipboard`. Used for import lines and the install command — the
 * first thing a developer reaches for when scanning a component library.
 */
export function CopyButton({
  value,
  label = "Copy",
  className,
}: {
  value: string;
  label?: string;
  className?: string;
}) {
  const { copy, copiedText } = useCopyToClipboard();
  const [flash, setFlash] = React.useState(false);
  const copied = flash && copiedText === value;

  React.useEffect(() => {
    if (!flash) return;
    const t = window.setTimeout(() => setFlash(false), 1600);
    return () => window.clearTimeout(t);
  }, [flash]);

  return (
    <button
      type="button"
      onClick={() => {
        void copy(value).then((ok) => ok && setFlash(true));
      }}
      aria-label={copied ? "Copied" : label}
      title={copied ? "Copied" : label}
      className={cn(
        "inline-flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground",
        "transition-colors duration-180 ease-smooth-out hover:bg-muted hover:text-foreground",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
        className,
      )}
    >
      {copied ? <CheckIcon className="size-3.5 text-success" /> : <CopyIcon className="size-3.5" />}
    </button>
  );
}

/** Colored chip naming a component's source layer (shadcn / brand / aisquare). */
export function LayerBadge({ meta, className }: { meta: LayerMeta; className?: string }) {
  return (
    <span
      title={meta.label}
      className={cn(
        "rounded-full border px-2 py-0.5 font-mono text-[10px] font-medium uppercase tracking-wider leading-none",
        meta.badgeClass,
        className,
      )}
    >
      {meta.badge}
    </span>
  );
}

/** "New" chip — shown only for components introduced in `LATEST_VERSION`. */
export function NewBadge({ since }: { since: string }) {
  if (since !== LATEST_VERSION) return null;
  return (
    <span
      title={`Added in ${LATEST_VERSION}`}
      className="rounded-full border border-success/30 bg-success/10 px-2 py-0.5 font-mono text-[10px] font-medium uppercase tracking-wider leading-none text-success"
    >
      New
    </span>
  );
}

/** Uppercase mono label — the Onyx `text-eyebrow` step, used above every title. */
export function Eyebrow({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <p className={cn("font-mono text-eyebrow uppercase text-muted-foreground", className)}>
      {children}
    </p>
  );
}

/**
 * The import line for a component, with a copy button — the one piece of
 * information a developer wants before anything else. Both halves come from the
 * registry (export names via `importNamesFor`, path via the layer metadata), so
 * it can't drift from the real import. Renders nothing for a showcase page whose
 * title names no single export.
 */
export function ImportLine({ entry }: { entry: DemoEntry }) {
  const names = importNamesFor(entry);
  if (names.length === 0) return null;

  const meta = layerMetaFor(entry.layer);
  const line = `import { ${names.join(", ")} } from "${meta.importPath}";`;

  return (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-card ps-3 pe-1.5 py-1.5">
      <code dir="ltr" className="min-w-0 flex-1 truncate font-mono text-caption text-foreground/80">
        {line}
      </code>
      <CopyButton value={line} label="Copy import" />
    </div>
  );
}
